/**
 * Page role indicator — STLDEV-1557
 *
 * Shows the role the current user is acting as in the page header. While a KA
 * holds an active delegation this reads `AC (Delegated)` instead of their
 * stored role, which the page payload still reports as KA.
 *
 * Display only. `AC (Delegated)` is not an RBAC role and is never written to
 * the user-role tables, so this must not be used to decide what the user may
 * do — the backend authorises every action independently.
 */
import React, { useEffect, useRef, useState } from 'react'
import { Stack, Typography } from '@mui/material'
import { TypographyProps } from '@mui/material/Typography'
import PersonIcon from '@mui/icons-material/Person'
import { fetchOwnDelegateStatus } from '@/service/DelegationManagementService'

export const DELEGATED_ROLE_LABEL = 'AC (Delegated)'

export interface RoleIndicatorProps {
  /** Stored role from the page payload, shown when no delegation is active. */
  role?: string
  iconSize?: number
}

const RoleIndicator: React.FC<RoleIndicatorProps> = ({
  role,
  iconSize = 30
}) => {
  const [isDelegate, setIsDelegate] = useState(false)
  const isMounted = useRef(true)

  // Resolved per mount rather than from the JWT: delegate status flips the
  // moment a delegation ends or its end date passes.
  useEffect(() => {
    isMounted.current = true

    fetchOwnDelegateStatus()
      .then(status => {
        if (isMounted.current) {
          setIsDelegate(status)
        }
      })
      // A badge failure must never blank out the role label.
      .catch(() => undefined)

    return () => {
      isMounted.current = false
    }
  }, [])

  return (
    <Stack display={'flex'} alignItems={'center'} flexDirection={'row'}>
      <PersonIcon
        sx={{ fontSize: iconSize, marginRight: 0.5 }}
        color='primary'
      />
      <Typography variant={'subtitle3' as TypographyProps['variant']}>
        {isDelegate ? DELEGATED_ROLE_LABEL : role}
      </Typography>
    </Stack>
  )
}

export default RoleIndicator
